import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, Home, Mail, PhoneCall } from 'lucide-react';
import BookingFormPopup from '../components/BookingForms/BookingFormPopup';

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingState = location.state || {};
  const hasBookingSelection = Boolean(
    bookingState.selectedVehicleData ||
    bookingState.initialRouteData?.vehicle ||
    bookingState.initialRouteData?.route ||
    bookingState.tripType
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!hasBookingSelection) {
      navigate('/', { replace: true });
    }
  }, [hasBookingSelection, navigate]);

  if (!hasBookingSelection) {
    return null;
  }

  const handleClose = () => {
    navigate(-1);
  };

  const handleSuccess = (response) => {
    console.log('Booking submitted successfully:', response);
    setIsSubmitted(true);
  };

  const handleError = (error) => {
    console.error('Booking submission failed:', error);
  };

  const supportPhone = '+91 73495 19354';
  const supportEmail = 'achalatours22@gmail.com';

  return (
    <main className={`min-h-screen px-4 py-10 ${isSubmitted ? 'bg-slate-50' : 'bg-gray-100'}`}>
      {isSubmitted ? (
        <section className="mx-auto w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
          <div className="grid md:grid-cols-[1.1fr_0.9fr]">
            <div className="bg-gray-950 px-6 py-10 text-white sm:px-10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15 ring-1 ring-green-400/30">
                <CheckCircle className="h-10 w-10 text-green-400" aria-hidden="true" />
              </div>
              <p className="mt-8 text-sm font-bold uppercase tracking-wide text-yellow-400">
                Booking enquiry received
              </p>
              <h1 className="mt-3 text-4xl font-black leading-tight text-white md:text-5xl">
                Thank you for choosing Plus Cabs.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-gray-300">
                Your booking enquiry has been received successfully. Our team will contact you soon with the trip confirmation and next details.
              </p>
            </div>

            <div className="px-6 py-10 sm:px-10">
              <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-green-700" aria-hidden="true" />
                  <div>
                    <h2 className="text-lg font-extrabold text-gray-950">We will contact you soon</h2>
                    <p className="mt-1 text-sm leading-6 text-gray-700">
                      Thank you for contacting Plus Cabs. For urgent help, reach us directly below.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-3">
                <a
                  href="tel:+917349519354"
                  className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-yellow-400 hover:shadow-md"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-yellow-100 text-yellow-700">
                    <PhoneCall className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-wide text-gray-500">Call us</span>
                    <span className="block text-base font-extrabold text-gray-950">{supportPhone}</span>
                  </span>
                </a>

                <a
                  href={`mailto:${supportEmail}`}
                  className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-yellow-400 hover:shadow-md"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                    <Mail className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-wide text-gray-500">Email</span>
                    <span className="block break-all text-base font-extrabold text-gray-950">{supportEmail}</span>
                  </span>
                </a>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 flex-none text-yellow-600" aria-hidden="true" />
                  <div>
                    <p className="font-bold text-gray-950">Next step</p>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Our booking team reviews your enquiry and shares availability, fare confirmation, and driver details.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-500 px-6 py-3 font-extrabold text-black shadow-sm transition hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                <Home className="h-5 w-5" aria-hidden="true" />
                Back to Home
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <div className="max-w-4xl mx-auto text-center mb-6">
            {/* <p className="text-sm font-bold text-yellow-600 uppercase tracking-wide">Plus Cabs Booking</p> */}
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">Complete Your Booking</h1>
            <p className="text-gray-600 mt-2">
              Your selected trip and vehicle details are pre-filled. Add your contact and travel details to submit.
            </p>
          </div>

        <BookingFormPopup
          isOpen
          onClose={handleClose}
          onSuccess={handleSuccess}
          onError={handleError}
          selectedVehicleData={bookingState.selectedVehicleData}
          tripType={bookingState.tripType || 'Airport Taxi'}
          initialRouteData={bookingState.initialRouteData}
          renderMode="page"
        />
        </>
      )}
    </main>
  );
};

export default BookingPage;
